import { Button, Stack, Typography } from '@mui/material'
import { SimplePageWrapper } from '~/@ui/simple-page-wrapper/SimplePageWrapper'
import { SliderWithButtons } from '~/@ui/slider/SliderWithButtons/SliderWithButtons'
import { useAlert } from '~/@ui/alert/useAlert'
import { useNavigate, useParams } from 'react-router-dom'
import { Form, useForm } from 'react-hook-form'
import { InfoBubble } from '~/@ui/info-bubble/InfoBubble'
import { RentDisplay } from './_components/RentDisplay'
import { useEffect, useState } from 'react'
import { getApi } from '~/@utils/api/getApi'
import { getErrorMessage } from '~/@utils/api/getErrorMessage'
import { calculatePensionDetails } from '~/@utils/calculator/calculatePensionDetails'
import { useCurrentUserData } from '~/@biz_account/useCurrentUserData/useCurrentUserData'
import { Header } from '~/@ui/header/Header'

export const SecondRentPage = () => {
  const navigate = useNavigate()
  const { showAlert } = useAlert()
  const { estateId = '' } = useParams()
  const { user } = useCurrentUserData()

  const userAge = user?._personalInformation?.age ?? 0
  const maxAgeSlider = 95 - userAge

  const [orderId, setOrderId] = useState('')
  const [pension, setPension] = useState(300)

  const { control, setValue, watch } = useForm({
    mode: 'onBlur',
    defaultValues: {
      years: 10,
      value: 100000,
    },
  })

  const valueWatch = watch('value')
  const yearsWatch = watch('years')

  const getEstate = async () => {
    try {
      const { data: response } =
        await getApi().ownerEstateAppApiControllerGetOwnerEstate(estateId)

      if ('success' in response) {
        setValue(
          'value',
          Math.floor(
            Math.min(
              Math.max(response.ownerEstate.apiEstimatedValue || 50000, 50000),
              350000
            ) / 5000
          ) * 5000
        )
        setOrderId(response.ownerEstate._ownerPensionOrder?.id || '')
        return
      }

      throw new Error('Nastala chyba pri načítavaní dát o nehnutelnsoti.')
    } catch (err) {
      console.error(err)
      showAlert('error', getErrorMessage(err))
    }
  }

  const handleSubmit = async () => {
    try {
      if (!orderId) {
        throw new Error('Nepodarilo sa načítať ID objednávky.')
      }

      const { data: result } =
        await getApi().ownerPensionOrderAppApiControllerUpdateOwnerPensionOrder(
          orderId,
          { payoutYears: yearsWatch, ownerEstimatedValue: valueWatch }
        )

      if ('success' in result) {
        navigate(`/vlastnik/dotaznik/potrebne-dokumenty/${orderId}`)
        return
      }

      throw new Error('Nasatala neznáma chyba pri nahrávaní odhadu.')
    } catch (err) {
      console.error(err)
      showAlert('error', getErrorMessage(err))
    }
  }

  useEffect(() => {
    const pension = calculatePensionDetails(
      yearsWatch,
      user?._personalInformation?.age ?? 0,
      valueWatch
    )

    setPension(pension.monthlyPensionValue)
  }, [yearsWatch, valueWatch, user?._personalInformation?.age])

  useEffect(() => {
    if (user) {
      setValue('years', 10 >= (user._personalInformation?.age ?? 0) ? 0 : 10)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  useEffect(() => {
    if (!estateId) {
      navigate('/vlastnik/dotaznik/adresa-nehnutelnosti')
      return
    }

    getEstate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <SimplePageWrapper
      onClickBack={() =>
        navigate(`/vlastnik/dotaznik/detaily-o-nehnutelnosti/${estateId}`)
      }>
      <Stack className="px-5 pb-28">
        <Header
          title="Druhý mesačný dôchodok"
          text="Približný odhad pre vašu orientáciu."
          className="mt-6"
        />

        <RentDisplay value={Math.floor(pension)} />

        <Form control={control} className="flex flex-col gap-3 mt-8">
          <Stack className="gap-5 px-4 pt-4 pb-3 bg-zGrey200 rounded-4d">
            <Stack className="flex-row justify-between">
              <Typography className="text-base font-semibold text-zBlack200">
                Odhadovaná hodnota nehnuteľnosti
              </Typography>
              <InfoBubble text="Vami odhadovaná hodnota nehnuteľnosti." />
            </Stack>
            <SliderWithButtons
              darkTheme
              className="gap-2"
              name={'value'}
              suffix="€"
              min={50000}
              max={350000}
              step={1000}
              hideMinMax
              control={control}
              useDynamicSteps
            />
          </Stack>

          <Stack className="gap-5 px-4 pt-4 pb-3 bg-zGrey200 rounded-4d">
            <Stack className="flex-row justify-between">
              <Typography className="text-base font-semibold text-zBlack200">
                Doba vypláciania
              </Typography>
              <InfoBubble text="Vami odhadovaná hodnota nehnuteľnosti." />
            </Stack>
            {userAge < 85 ? (
              <SliderWithButtons
                darkTheme
                className="gap-2"
                name={'years'}
                suffix="rokov"
                min={10}
                max={maxAgeSlider}
                step={1}
                hideMinMax
                control={control}
              />
            ) : (
              <Stack className="self-center">
                <Typography className="text-[32px]/[36px] font-black text-zBlack200">
                  doživotne
                </Typography>
              </Stack>
            )}
          </Stack>
        </Form>

        <Stack className="gap-3 mt-[72px]">
          <Button
            variant="contained"
            className="w-fit h-14 self-center px-10"
            onClick={handleSubmit}>
            Pokračovať
          </Button>
        </Stack>
      </Stack>
    </SimplePageWrapper>
  )
}
